import React, { useState, useEffect, forwardRef, useRef, useMemo } from 'react';
import Select, { components, FormatOptionLabelMeta, GroupBase, MultiValue, SelectInstance, SingleValue } from "react-select";
import { VariableSizeList as List } from 'react-window';
import { fetchItems } from '../../../api'; // Adjust the import path as needed
import { ItemForm } from './ItemForm'; // Adjust the import path as needed
import CustomModal from '../../widgets/components/CustomModal';
import { Item } from '../../../types';
import { mergeSelectStyles } from '../../widgets/components/SelectStyles';

interface OptionType {
  value: string;
  label: string;
  rate?: number;
  description?: string | null;
  otherText?: string;
}

interface ItemPickerProps {
  selectedItem?: string | string[];
  onItemChange: (item: Item | Item[]) => void; // Support single or multi-select
  onInputChange?: (input: string) => void;
  isMulti?: boolean; // New optional prop for multi-select functionality
  salesFlag?: boolean;
  purchaseFlag?: boolean;
  onSave?: (item: Item) => void;
  placeHolder?: string;
  onFocus?: boolean;
}

// Base height for options
const BASE_OPTION_HEIGHT = 32;
// Additional height per line of text
const BASE_PADDING = 16;
const LINE_HEIGHT = 24;
// Maximum number of visible options before scrolling
const MAX_VISIBLE_OPTIONS = 8;


// Utility: measure text width using canvas
const measureTextWidth = (text: string, font = '14px Inter, sans-serif') => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

// Dynamic row height calculator
const getItemHeight = (
  index: number,
  options: OptionType[],
  menuWidth = 260 // default, you can override with actual dropdown width
) => {
  const option = options[index];
  if (!option) return LINE_HEIGHT + BASE_PADDING;

  let height = BASE_PADDING;

  // === Label lines ===
  const labelText = option.label || '';
  const textWidth = measureTextWidth(labelText);
  const availableWidth = menuWidth - 24; // account for padding inside item
  const lines = Math.max(1, Math.ceil(textWidth / availableWidth));

  height += lines * LINE_HEIGHT;

  // === Rate line ===
  if (option.value !== 'add_new') {
    height += LINE_HEIGHT;
  }

  // === Description line ===
  if (option.description && option.value !== 'add_new') {
    height += LINE_HEIGHT;
  }

  return height;
};


// Virtualized Option component
// Replace the VirtualizedOption component with this version that supports dark mode:

const VirtualizedOption = ({ index, style, data }: any) => {
  const { options, formatOptionLabel, selectOption, focusedOptionIndex } = data;
  const option = options[index];

  const isSelected = data.selectedOptions?.some((selected: OptionType) => selected.value === option.value);
  const isFocused = index === focusedOptionIndex;

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'flex-start',
        padding: '8px 12px',
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
      className={`
        ${isFocused ? 'bg-primary-subtle text-primary-emphasis' : ''}
        ${isSelected ? 'bg-success-subtle text-success-emphasis' : ''}
        ${!isFocused && !isSelected ? 'bg-body text-body' : ''}
        border-bottom border-secondary-subtle
      `}
      onClick={() => selectOption(option)}
      onMouseEnter={() => data.setFocusedOption && data.setFocusedOption(index)}
    >
      {/* MBAI-254: allow long item names to wrap instead of clipping */}
      <div style={{
        width: '100%',
        lineHeight: '1.3',
      }}>
        {formatOptionLabel(option, { context: 'menu' })}
      </div>
    </div>
  );
};

// Also update the formatOptionLabel function to support dark mode colors:

const formatOptionLabel = (
  { value, label, rate, description }: OptionType,
  { context }: { context: 'menu' | 'value' }
) => {
  return context === 'menu' ? (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    }}>
      <strong
        style={{
          fontSize: '14px',
          lineHeight: '1.3',
          wordBreak: 'break-word',
          margin: 0,
        }}
        className="text-gray-900 dark:text-gray-100"
      >
        {label}
      </strong>
      {value !== 'add_new' && (
        <span
          style={{
            fontSize: '14px',
            lineHeight: '1.2',
            margin: 0,
          }}
          className="text-gray-600 dark:text-gray-400"
        >
          {'Rate:'.toTranslatedDisplay()} {rate}
        </span>
      )}
      {description && value !== 'add_new' && (
        <span
          style={{
            fontSize: '11px',
            lineHeight: '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            margin: 0,
          }}
          className="text-gray-500 dark:text-gray-500"
        >
          {'Description:'.toTranslatedDisplay()} {description}
        </span>
      )}
    </div>
  ) : (
    <span
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      className="text-gray-900 dark:text-gray-100"
    >
      {label}
    </span>
  );
};

const customStyles = mergeSelectStyles({
  option: () => ({
    // virtualized list renders its own row UI
  }),
  menuList: () => ({
    // virtualized list manages its own scroll container
  }),
  singleValue: (styles: any, { data }: any) => ({
    ...styles,
    color: 'var(--bs-gray-700)',
    ...(data.label)
  }),
  placeholder: (styles: any) => ({
    ...styles,
    color: 'var(--bs-gray-500)',
  }),
});

// Virtualized MenuList component
const VirtualizedMenuList = (props: any) => {
  const { options, children, getValue, selectOption } = props;
  const selectedOptions = getValue();

  // State to track focused option for keyboard navigation
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);

  // Calculate total height for the dropdown
  const totalHeight = Math.min(
    options.reduce((acc: number, _: OptionType, index: number) => acc + getItemHeight(index, options), 0),
    MAX_VISIBLE_OPTIONS * BASE_OPTION_HEIGHT * 1.5 // Max height cap
  );

  // Get formatOptionLabel from the parent component
  const formatOptionLabel = props.selectProps?.formatOptionLabel;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedOptionIndex((prev) => Math.min(prev + 1, options.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedOptionIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (options[focusedOptionIndex]) {
            selectOption(options[focusedOptionIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedOptionIndex, options, selectOption]);

  // Reset focused index when options change
  useEffect(() => {
    setFocusedOptionIndex(0);
  }, [options]);

  return (
    <div>
      <List
        height={totalHeight}
        width="100%"
        itemCount={options.length}
        itemSize={(index) => getItemHeight(index, options)}
        itemData={{
          options,
          formatOptionLabel,
          selectOption,
          selectedOptions,
          focusedOptionIndex,
          setFocusedOption: setFocusedOptionIndex,
        }}
      >
        {VirtualizedOption}
      </List>
    </div>
  );
};

export const ItemPicker = forwardRef<HTMLDivElement, ItemPickerProps>(
  ({ onItemChange, selectedItem, onInputChange, isMulti = false, salesFlag, purchaseFlag, onSave, placeHolder, onFocus }, ref) => {
    const [fetchedItems, setFetchedItems] = useState<Item[]>([]);
    const [items, setItems] = useState<OptionType[]>([]);
    const [selectedOption, setSelectedOption] = useState<OptionType | OptionType[] | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const internalSelectRef = useRef<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>(null);

    useEffect(() => {
      // Focus the select component on mount
      if (internalSelectRef.current && onFocus) {
        internalSelectRef.current.focus();
      }
    }, [onFocus]);

    useEffect(() => {
      const getItems = async () => {
        try {
          const fetchedItems: Item[] = await fetchItems();

          // Apply filters based on salesFlag and purchaseFlag
          let filteredItems = fetchedItems;
          // MBAI-406: archived items are hidden from new transaction pickers
          filteredItems = filteredItems.filter(item => item.isActive !== false);
          // Only exclude if explicitly false
          if (salesFlag === true) {
            filteredItems = filteredItems.filter(item => item.salesFlag !== false);
          }

          if (purchaseFlag === true) {
            filteredItems = filteredItems.filter(item => item.purchaseFlag !== false);
          }

          const options = filteredItems.map(item => ({
            value: item.itemId,
            label: item.itemName || '',
            rate: item.itemRate || 0,
            description: item.itemTxt || '',
            otherText: JSON.stringify(item)
          }));

          setFetchedItems(filteredItems);
          setItems(options);
        } catch (error) {
          console.error('Error fetching items:', error);
        }
      };

      getItems();
    }, [salesFlag, purchaseFlag]);

    // Memoized filtered options for better performance
    const filteredOptions = useMemo(() => {
      const addNewOption = { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), rate: 0, description: '' };

      if (!inputValue.trim()) {
        return [addNewOption, ...items];
      }

      const filtered = items.filter(option => {
        const labelMatches = option.label.toLowerCase().includes(inputValue.toLowerCase());
        const descriptionMatches = option.otherText?.toLowerCase().includes(inputValue.toLowerCase()) || false;
        return labelMatches || descriptionMatches;
      });

      return [addNewOption, ...filtered];
    }, [items, inputValue]);

    const customStyles = mergeSelectStyles({
      option: () => ({
        // virtualized list renders its own row UI
      }),
      menuList: () => ({
        // virtualized list manages its own scroll container
      }),
      valueContainer: (styles: any) => ({
        ...styles,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: '1.3',
        maxHeight: 'none',
        overflow: 'visible',
      }),
      singleValue: (styles: any) => ({
        ...styles,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'anywhere',
        textOverflow: 'unset',
        overflow: 'visible',
        maxWidth: '100%',
        position: 'relative',
        color: 'var(--bs-gray-700)',
      }),
      multiValue: (styles: any) => ({
        ...styles,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        maxWidth: '100%',
      }),
      multiValueLabel: (styles: any) => ({
        ...styles,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        color: 'var(--bs-gray-700)',
      }),
    });

    // Custom filtering - now handled by filteredOptions memo
    const filterOption = () => true; // Always return true since filtering is handled by useMemo

    // This will control how the options are displayed in the dropdown (label, rate, description)
    const formatOptionLabel = (
      { value, label, rate, description }: OptionType,
      { context }: { context: 'menu' | 'value' }
    ) => {
      return context === 'menu' ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}>
          <strong style={{
            fontSize: '14px',
            lineHeight: '1.3',
            wordBreak: 'break-word',
            margin: 0,
          }}>
            {label}
          </strong>
          {value !== 'add_new' && (
            <span style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.2',
              margin: 0,
            }}>
              {'Rate:'.toTranslatedDisplay()} {rate}
            </span>
          )}
          {description && value !== 'add_new' && (
            <span style={{
              fontSize: '11px',
              color: '#888',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              margin: 0,
            }}>
              {'Description:'.toTranslatedDisplay()} {description}
            </span>
          )}
        </div>
      ) : (
        <span style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        }}>
          {label}
        </span>
      );
    };

    // This will ensure only the label (item name) is displayed in the input field after selection
    const getOptionLabel = (option: OptionType) => option.label;

    const handleInputChange = (input: string, actionMeta: { action: string }) => {
      if (actionMeta.action === 'input-change') {
        setInputValue(input);
        if (onInputChange) onInputChange(input || '');
      }
    };

    useEffect(() => {
      if (selectedItem) {
        const defaultOption = isMulti
          ? filteredOptions.filter(option => (selectedItem as string[]).includes(option.value))
          : filteredOptions.find(option => option.value === selectedItem) || null;
        if (defaultOption) {
          setSelectedOption(
            isMulti
              ? [...(defaultOption as readonly OptionType[])]
              : defaultOption as SingleValue<OptionType>
          );
        } else {
          setSelectedOption({ label: selectedItem as string, value: '' } as SingleValue<OptionType>);
        }
      } else {
        setSelectedOption(null);
      }
    }, [selectedItem, filteredOptions]);

    const handleSelectChange = (newValue: SingleValue<OptionType> | MultiValue<OptionType>) => {
      // Check if "add_new" option was selected
      if (isMulti) {
        const newOptions = newValue as OptionType[];

        if (newOptions.some(option => option.value === 'add_new')) {
          setIsModalOpen(true);
          return;
        }

        // Map the selected options to fetched items
        const selectedItems = newOptions.map(option =>
          fetchedItems.find(item => item.itemId === option.value) || new Item()
        );
        onItemChange(selectedItems);
      } else {
        const newOption = newValue as OptionType;

        if (newOption && newOption.value === 'add_new') {
          setIsModalOpen(true);
          return;
        }

        // Handle single item selection
        setSelectedOption(newOption);
        if (newOption?.value) {
          const selectedSingleItem = fetchedItems.find(item => newOption?.value === item.itemId);
          if (selectedSingleItem) onItemChange(selectedSingleItem);
        }
      }
    };

    const handleModalClose = () => {
      setIsModalOpen(false);
    };

    const handleAddNew = async (newOption: OptionType) => {
      setIsModalOpen(false);
      setItems(prevItems => [...prevItems, newOption]);
      setSelectedOption(newOption);
      const newItems = await fetchItems();
      const foundItem = newItems.find(item => item.itemId === newOption.value) || new Item();
      onItemChange(foundItem);
    };

    return (
      <div className="searchable-picker">
        <Select
          className="react-select-styled react-select"
          classNamePrefix="react-select"
          value={selectedOption}
          ref={internalSelectRef}
          options={filteredOptions}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={getOptionLabel}
          styles={customStyles}
          placeholder={placeHolder || "Select Item"}
          onChange={handleSelectChange}
          isClearable
          onInputChange={handleInputChange}
          isMulti={isMulti}
          tabSelectsValue={false}
          components={{
            MenuList: VirtualizedMenuList,
            Option: () => null // Remove default Option component since we're using virtualized list
          }}
          filterOption={filterOption}
          maxMenuHeight={MAX_VISIBLE_OPTIONS * BASE_OPTION_HEIGHT * 1.5}
        />
        {isModalOpen && (
          <CustomModal
            title={`Add New Item`}
            isVisible={isModalOpen}
            onClose={handleModalClose}
            body={
              <ItemForm
                onModal
                onSave={(newItem: Item) => {
                  handleAddNew({ value: newItem.itemId, label: newItem.itemName || '' });
                }}
                onExit={handleModalClose}
              />
            }
            id={''}
            size="large"
            noScaffolding
            onSave={() => { }}
          />
        )}
      </div>
    );
  }
);